import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/components'
import { Button } from '@/ui/components'
import { Badge } from '@/ui/components'
import { WaveLoader } from '@/ui/components'
import { Compass, Sparkles, RefreshCw, Route, CloudRain, Flag } from '@/ui/icons'
import { RangerChatModal } from '@/modules/ai'
import type { FeedCard as FeedCardType } from '@/shared/api'
import { FeedCard } from './FeedCard'
import { EventCard } from './EventCard'
import { AiRecommendationCard } from './AiRecommendationCard'
import { AiUpsellCard } from './AiUpsellCard'
import { WeekendSuggestionCard } from './WeekendSuggestionCard'
import { WeekendSuggestionSetupCard } from './WeekendSuggestionSetupCard'
import { FeedRow } from './FeedRow'

const EXAMPLE_ROWS = [
  { icon: Route, title: 'Plan your route', subtitle: '3 days until departure — no waypoints added yet' },
  { icon: CloudRain, title: 'Weather check', subtitle: 'Rain likely the weekend of your trip' },
  { icon: Flag, title: 'Community event nearby', subtitle: 'Regional overlanding rally · 2 weeks out' },
]

interface FeedListProps {
  cards: FeedCardType[]
  isLoading: boolean
  error: string | null
  onRefresh: () => void
}

export function FeedList({ cards, isLoading, error, onRefresh }: FeedListProps) {
  const [rangerOpen, setRangerOpen] = useState(false)
  const [rangerTripId, setRangerTripId] = useState<string | undefined>(undefined)

  const openRanger = (tripId?: string) => {
    setRangerTripId(tripId)
    setRangerOpen(true)
  }

  const renderRow = (card: FeedCardType) => {
    if (card.kind === 'ai_recommendation') {
      return <AiRecommendationCard key={card.id} card={card} onAskRanger={openRanger} />
    }
    if (card.kind === 'ai_upsell') return <AiUpsellCard key={card.id} card={card} />
    if (card.kind === 'weekend_suggestion') return <WeekendSuggestionCard key={card.id} card={card} />
    if (card.kind === 'weekend_suggestion_setup') {
      return <WeekendSuggestionSetupCard key={card.id} card={card} onHomeBaseSaved={onRefresh} />
    }
    if (card.kind === 'event') return <EventCard key={card.id} card={card} />
    return <FeedCard key={card.id} card={card} />
  }

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-adventure-600 dark:text-adventure-400" />
              <CardTitle className="text-lg font-semibold text-earth-900 dark:text-foreground">
                Your Feed
              </CardTitle>
            </div>
            {cards.length > 0 && <Badge variant="secondary">{cards.length}</Badge>}
          </div>
          <p className="text-sm text-earth-700 dark:text-earth-400 mt-1">
            What to do next, recommendations from Ranger, and what’s coming up.
          </p>
        </CardHeader>

        <CardContent>
          {isLoading && cards.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <WaveLoader />
            </div>
          ) : error && cards.length === 0 ? (
            <div className="text-center py-10">
              <h3 className="text-base font-medium text-earth-900 dark:text-foreground mb-1">
                Couldn’t load your feed
              </h3>
              <p className="text-sm text-earth-700 dark:text-earth-400">{error}</p>
              <Button variant="outline" size="sm" className="mt-4" onClick={onRefresh}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Try again
              </Button>
            </div>
          ) : cards.length === 0 ? (
            <div className="text-center py-8">
              <Compass className="mx-auto mb-3 h-12 w-12 text-earth-300 dark:text-earth-600" />
              <h3 className="text-lg font-medium text-earth-900 dark:text-foreground mb-2">
                Your feed is clear
              </h3>
              <p className="text-sm text-earth-700 dark:text-earth-400 mb-4">
                Plan a trip and this is where Ranger will start telling you what to do next —
                route gaps, weather windows, gear you're missing, and events nearby.
              </p>
              <Button asChild>
                <Link to="/trips/new">Plan a trip</Link>
              </Button>

              <div className="mt-8 max-w-sm mx-auto text-left opacity-50 pointer-events-none select-none">
                <p className="text-xs font-medium text-earth-500 mb-1 text-center">Example</p>
                <ul className="flex flex-col border border-dashed border-border rounded-lg px-1">
                  {EXAMPLE_ROWS.map((row) => (
                    <FeedRow key={row.title} icon={row.icon} title={row.title} subtitle={row.subtitle} />
                  ))}
                </ul>
              </div>
            </div>
          ) : (
            <ul className="flex flex-col">{cards.map(renderRow)}</ul>
          )}
        </CardContent>
      </Card>

      <RangerChatModal
        isOpen={rangerOpen}
        onClose={() => setRangerOpen(false)}
        tripId={rangerTripId}
      />
    </>
  )
}
